import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useFetch, usePromise } from '@rad/hooks';
import { useNotify } from '@rad/contexts/notifications';
import { staticForm, adjustForm, formValues, unsafeMapForm, formWithErrors } from '@rad/components/dynamic-form/common';
import { getMeasures, getMeasure, createMeasure, updateMeasure } from '../types';
import LayoutForm from '../layouts/form';
const constructForm = () => staticForm([
    [
        {
            'id': 'number',
            'title': 'Number',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'plain',
            },
            'required': true,
        },
        {
            'id': 'title',
            'title': 'Title',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'plain',
            },
            'required': true,
        },
        {
            'id': 'description',
            'title': 'Description',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'multiline',
            },
            'required': true,
        },
        {
            'id': 'filter',
            'title': 'Filter',
            'kind': 'text',
            'value': '',
            'settings': { 'special': 'multiline' },
            'required': false,
        },
        {
            'id': 'category',
            'title': 'Category',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure-category',
                },
            },
            'required': true,
        },
        {
            'id': 'category_new',
            'title': 'New Category',
            'kind': 'text',
            'value': '',
            'settings': {
                'special': 'plain',
            },
            'required': true,
        },
    ],
    [
        {
            'id': 'score_type',
            'title': 'Score Type',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure-score-type',
                },
            },
            'required': true,
        },
        {
            'id': 'direction',
            'title': 'Direction',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure-direction',
                },
            },
            'required': true,
        },
        {
            'id': 'precision',
            'title': 'Precision',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure-precision-m'
                },
            },
            'required': true,
        },
        {
            'id': 'aggregation',
            'title': 'Aggregation',
            'kind': 'select',
            'value': '',
            'settings': {
                'source': {
                    'id': 'measure-aggregation',
                    'has_group': true,
                },
            },
            'required': true,
        },
        // {
        //   'id':'value_per_point',
        //   'title':'Value per Point',
        //   'kind':'number',
        //   'value':'',
        //   'settings':{
        //   },
        //   'required':true,
        // },
    ],
]);
const logic = (m) => (x, values) => {
    switch (x.id) {
        case 'category_new':
            return { ...x, 'hidden': !!values['category'], 'value': !!values['category'] ? '' : x.value };
        case 'value_per_point':
            return { ...x, 'hidden': !['Meeting'].includes(values['aggregation']) };
        case 'threshold':
            return {
                ...x,
                'title': values['aggregation'] === 'Sliding Scale' ? 'Minimum Value' : 'Threshold',
                'hidden': ([null, 'Meeting', 'Reduce Score', 'Circuit Breaker'].includes(values['aggregation'])
                    || ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type'])),
            };
        case 'target':
            return {
                ...x,
                'title': values['aggregation'] === 'Sliding Scale' ? 'Baseline for Credit' : 'Target',
                'hidden': ([null, 'Meeting', 'Reduce Score', 'Circuit Breaker'].includes(values['aggregation'])
                    || ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type'])),
            };
        case 'max':
            return {
                ...x,
                'title': values['aggregation'] === 'Sliding Scale' ? 'Full Credit' : 'Max',
                'hidden': ([null, 'Meeting', 'Reduce Score', 'Circuit Breaker'].includes(values['aggregation'])
                    || ['Pass/Fail', 'Threshold/Target/Max'].includes(values['score_type'])),
            };
        default:
            return { ...x, 'disabled': false };
    }
};
const MeasureNew = (props) => {
    const notify = useNotify();
    const [measuresPromise] = useFetch(getMeasures());
    const [stateForm, setForm] = React.useState(constructForm());
    const adjustedForm = adjustForm(logic(undefined))(stateForm);
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await createMeasure({
            ...formValues(stateForm),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Measure added successfully!' });
            window.location.hash = `#/admin/measures`;
        }
        else {
            setErrors(result);
        }
    };
    { /* {measures && values['measure'] && measures.find((x) => x.id === values['measure'])?.description} */ }
    return _jsx(LayoutForm, { title: "Add Measure", form: formWithErrors(adjustedForm, stateErrors), onChange: setForm, onSubmit: submit });
};
const MeasureEdit = (props) => {
    const notify = useNotify();
    const [measurePromise] = useFetch(getMeasure(props.id));
    const measure = usePromise(measurePromise);
    const [stateForm, setForm] = React.useState(constructForm());
    const adjustedForm = adjustForm(logic(measure || undefined))(stateForm);
    const [stateErrors, setErrors] = React.useState(undefined);
    const submit = async (e) => {
        e.preventDefault();
        const [valid, result] = await updateMeasure({
            'id': props.id,
            ...formValues(stateForm),
        });
        if (valid) {
            notify({ 'kind': 'success', 'message': 'Measure added successfully!' });
            window.location.hash = `#/admin/measures`;
        }
        else {
            setErrors(result);
        }
    };
    React.useEffect(() => {
        setForm(unsafeMapForm(stateForm, (x) => {
            switch (x.id) {
                case 'number':
                    return { ...x, 'value': measure?.number || '' };
                case 'title':
                    return { ...x, 'value': measure?.title || '' };
                case 'category':
                    return { ...x, 'value': measure?.category || '' };
                case 'description':
                    return { ...x, 'value': measure?.description || '' };
                case 'score_type':
                    return { ...x, 'value': measure?.score_type || '' };
                case 'direction':
                    return { ...x, 'value': measure?.direction || '' };
                case 'precision':
                    return { ...x, 'value': measure ? String(measure.precision) : '' };
                case 'aggregation':
                    return { ...x, 'value': measure?.aggregation || '' };
                case 'filter':
                    return { ...x, 'value': measure?.filter || '' };
                default:
                    return x;
            }
        }));
    }, [measure]);
    if (measure === undefined) {
        return _jsx(_Fragment, { children: "Loading..." });
    }
    else if (measure === null) {
        return _jsx(_Fragment, { children: "Not Found" });
    }
    else {
        return _jsx(LayoutForm, { toolbarRight: _jsx(_Fragment, { children: _jsx("a", { href: "#/measures/request", className: "btn btn-sm btn-primary", children: "Request New Measure" }) }), title: _jsxs(_Fragment, { children: ["Edit Measure ", _jsx("em", { children: measure.display })] }), form: formWithErrors(adjustedForm, stateErrors), onChange: setForm, onSubmit: submit });
    }
};
const MeasureScreen = (props) => props.id !== undefined && props.edit
    ? _jsx(MeasureEdit, { id: props.id })
    : props.id !== undefined
        ? _jsx(_Fragment, { children: "Not Imlemented" })
        : _jsx(MeasureNew, {});
export default MeasureScreen;
